import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { CommentWall } from "@/components/CommentWall";
import { DemoPicker } from "@/components/DemoPicker";
import { Button } from "@/components/ui/button";
import ProductPreview from "@/components/ProductPreview";
import AnimatedLogo from "@/components/AnimatedLogo";
import {
  ArrowRight, GanttChartSquare, ShieldAlert,
  Users, Sparkles, CalendarClock, LayoutDashboard, CheckCircle2, BarChart3, FolderOpen, LayoutGrid, Building2, PlayCircle, Clapperboard, Wand2, Zap,
} from "lucide-react";

const FEATURES = [
  { icon: GanttChartSquare, color: "#3b82f6", title: "Visual timelines", desc: "Turn a plan into a Gantt/roadmap timeline with dependencies and slip propagation.", to: "/go/roadmap" },
  { icon: BarChart3, color: "#EC4899", title: "Reports & dashboards", desc: "Portfolio roll-ups, health charts, and executive readouts across every project.", to: "/go/reports" },
  { icon: CalendarClock, color: "#F59E0B", title: "Progress true-ups", desc: "Owners submit, PMs confirm — on your cadence. Gray until vetted, blue when done.", to: "/dashboard" },
  { icon: ShieldAlert, color: "#EF4444", title: "Risks & action items", desc: "P×I risk matrix plus a full action-item register with late/pending/done tracking.", to: "/go/risks" },
  { icon: Users, color: "#8b5cf6", title: "RACI & resources", desc: "Industry-standard RACI matrix and a reusable resource library with over-allocation leveling.", to: "/go/teams" },
  { icon: Sparkles, color: "#10B981", title: "AI copilot", desc: "Generate best-practice project plans, meeting briefs, and weekly executive readouts in seconds.", to: "/dashboard?new=1" },
];

export default function Landing() {
  const nav = useNavigate();
  const { user } = useAuth();

  return (
    <div className="min-h-screen text-white overflow-x-hidden relative" style={{ background: "#050b20" }}>
      {/* Ambient blue glows — same palette as the logo backdrop so everything is one field of blue */}
      <div aria-hidden className="pointer-events-none fixed inset-0" style={{ background: "radial-gradient(90% 60% at 50% -10%, #0d2350 0%, #071232 45%, #050b20 100%)" }} />
      <div aria-hidden className="pointer-events-none fixed -top-40 -left-40 h-[36rem] w-[36rem] rounded-full blur-3xl" style={{ background: "radial-gradient(circle, rgba(37,99,235,0.28), transparent 70%)" }} />
      <div aria-hidden className="pointer-events-none fixed top-1/3 -right-40 h-[34rem] w-[34rem] rounded-full blur-3xl" style={{ background: "radial-gradient(circle, rgba(37,99,235,0.18), transparent 70%)" }} />

      <div className="relative z-10">
        {/* Nav */}
        <header className="sticky top-0 z-40 border-b border-white/10 bg-[#050b20]/70 backdrop-blur">
          <div className="max-w-[1200px] mx-auto px-6 h-16 flex items-center justify-between">
            <button onClick={() => nav("/")} className="flex items-center gap-2 group" data-testid="landing-brand-button">
              <span className="font-display text-2xl font-extrabold tracking-tight text-white">Project Management <span className="text-[#7ea6ff]">Orchestrator</span></span>
            </button>
            <div className="flex items-center gap-2">
              <Button variant="ghost" className="text-white hover:bg-white/10 hover:text-white" onClick={() => nav("/pricing")} data-testid="landing-pricing-button">Pricing</Button>
              {user ? (
                <Button className="gap-2" onClick={() => nav("/dashboard")} data-testid="landing-myprojects-button"><LayoutDashboard className="h-4 w-4" /> My projects</Button>
              ) : (
                <>
                  <Button variant="ghost" className="text-white hover:bg-white/10 hover:text-white" onClick={() => nav("/login")} data-testid="landing-signin-button">Sign in</Button>
                  <Button className="gap-2" onClick={() => nav("/register")} data-testid="landing-getstarted-button">Get started <ArrowRight className="h-4 w-4" /></Button>
                </>
              )}
            </div>
          </div>
        </header>

        {/* Hero — details left, logo right (both on the same blue field) */}
        <section className="max-w-[1200px] mx-auto px-6 pt-12 pb-16 grid lg:grid-cols-2 gap-12 items-center">
          <div className="animate-in fade-in slide-in-from-bottom-4 duration-700">
            <span className="overline text-[#8fb2ff] inline-flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-[#8fb2ff]" /> Plan. Manage. Orchestrate.</span>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight mt-4 leading-[1.05]">
              Turn scope into <span className="text-[#7ea6ff]">action</span>.
            </h1>
            <p className="text-base text-slate-300 mt-5 max-w-xl">
              Project Management Orchestrator helps project managers build project ecosystems all in one place.
            </p>
            {user && (
              <span className="flex flex-wrap items-center gap-x-5 gap-y-1 mt-5">
                <button onClick={() => nav("/guide")}
                  className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#8fb2ff] hover:text-white transition-colors underline underline-offset-4"
                  data-testid="welcome-get-started-link">
                  <PlayCircle className="h-4 w-4" /> New here? Open the Get Started guide
                </button>
              </span>
            )}
            <div className="flex flex-wrap items-center gap-3 mt-8">
              {user ? (
                <>
                  {["supervisor", "admin"].includes(user.role) && (
                    <Button size="lg" className="gap-2 h-12 px-6 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => nav("/pmo")} data-testid="hero-pmo-view-button"><Building2 className="h-4 w-4" /> PMO</Button>
                  )}
                  <Button size="lg" className="gap-2 h-12 px-6 bg-violet-600 hover:bg-violet-700 text-white" onClick={() => nav("/portfolio")} data-testid="hero-portfolio-view-button"><LayoutGrid className="h-4 w-4" /> Portfolio</Button>
                  <Button size="lg" className="gap-2 h-12 px-6" onClick={() => nav("/home")} data-testid="hero-project-view-button"><FolderOpen className="h-4 w-4" /> Project</Button>
                  <Button size="lg" variant="outline" className="h-12 px-6 border-white/30 bg-white/5 text-white hover:bg-white/15 hover:text-white" onClick={() => nav("/dashboard?new=1")} data-testid="hero-new-project-button">Start a new project</Button>
                  <DemoPicker />
                </>
              ) : (
                <>
                  <Button size="lg" className="gap-2 h-12 px-6" onClick={() => nav("/dashboard?new=1")} data-testid="hero-new-project-button">Start a New Project Here <ArrowRight className="h-4 w-4" /></Button>
                  <DemoPicker />
                  <Button size="lg" variant="outline" className="h-12 px-6 border-white/30 bg-white/5 text-white hover:bg-white/15 hover:text-white" onClick={() => nav("/login")} data-testid="hero-signin-button">I have an account</Button>
                </>
              )}
            </div>
            <div className="flex flex-wrap gap-x-6 gap-y-2 mt-8 text-sm text-slate-300">
              {["AI plan generation", "Critical path & health", "Weekly exec summaries"].map((t) => (
                <span key={t} className="inline-flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-[#7ea6ff]" /> {t}</span>
              ))}
            </div>
            <CommentWall />
          </div>

          {/* Logo on the right — no box, blends into the blue */}
          <div className="flex justify-center lg:justify-end animate-in fade-in duration-1000" data-testid="welcome-top-logo">
            <AnimatedLogo onDark className="max-w-md w-full" />
          </div>
        </section>

        {/* Plan Wizard callout */}
        <section className="max-w-[1200px] mx-auto px-6 pb-20">
          <button onClick={() => nav("/build")} className="group w-full text-left rounded-2xl border border-[#7ea6ff]/30 bg-gradient-to-r from-[#0d2350] to-[#071232] p-8 sm:p-10 relative overflow-hidden hover:border-[#7ea6ff]/60 transition-colors" data-testid="landing-plan-wizard-banner">
            <Wand2 className="absolute -right-4 -bottom-4 h-36 w-36 opacity-[0.07] text-[#7ea6ff] rotate-12" aria-hidden />
            <span className="inline-flex items-center gap-1.5 text-[11px] font-bold tracking-[0.2em] text-[#8fb2ff] uppercase"><Sparkles className="h-3.5 w-3.5" /> New · Plan Wizard</span>
            <h2 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-white mt-3">One date in. A full plan out.</h2>
            <p className="text-slate-300 mt-3 max-w-2xl">Give it a start date — or just your deadline — and the wizard builds every task, duration, hand-off and owner in seconds. Too late to start? It compresses the plan to fit the time you have left.</p>
            <span className="inline-flex items-center gap-2 mt-5 text-sm font-semibold text-[#8fb2ff] group-hover:text-white transition-colors">Try the Plan Wizard <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" /></span>
          </button>
        </section>

        {/* Product preview showcase */}
        <section className="max-w-[1200px] mx-auto px-6 pb-20">
          <div className="relative animate-in fade-in duration-1000">
            <ProductPreview />
          </div>
        </section>

        {/* Features */}
        <section className="border-t border-white/10">
          <div className="max-w-[1200px] mx-auto px-6 py-20">
            <div className="max-w-2xl">
              <h2 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-white">Everything a PM needs, nothing they don't.</h2>
              <p className="text-slate-300 mt-3">From first scope to weekly stakeholder readout — one connected workflow.</p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-12">
              {FEATURES.map((f) => (
                <button key={f.title} onClick={() => nav(f.to)} className="group text-left border border-white/10 rounded-xl p-6 bg-white/[0.04] hover:-translate-y-1 hover:bg-white/[0.07] hover:border-white/25 transition-all duration-200" data-testid={`feature-${f.title.toLowerCase().replace(/[^a-z]+/g, "-").replace(/-$/, "")}`}>
                  <div className="h-11 w-11 rounded-lg flex items-center justify-center mb-4 transition-transform group-hover:scale-110 shadow-sm" style={{ background: f.color, boxShadow: `0 4px 14px -2px ${f.color}88` }}>
                    <f.icon className="h-5 w-5 text-white" strokeWidth={2.5} />
                  </div>
                  <h3 className="font-display text-lg font-bold flex items-center gap-1.5 text-white">{f.title} <ArrowRight className="h-4 w-4 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all text-[#7ea6ff]" /></h3>
                  <p className="text-sm text-slate-400 mt-1.5 leading-relaxed">{f.desc}</p>
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="max-w-[1200px] mx-auto px-6 py-20">
          <div className="rounded-2xl border border-white/10 bg-white/[0.05] p-10 sm:p-14 text-center relative overflow-hidden backdrop-blur-sm">
            <LayoutDashboard className="absolute -right-6 -bottom-6 h-40 w-40 opacity-[0.06] text-white" aria-hidden />
            <h2 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-white">Ready to forge your plan?</h2>
            <p className="mt-3 text-slate-300 max-w-lg mx-auto">Create your workspace and let the AI draft a best-practice plan tailored to your industry in under a minute.</p>
            <Button size="lg" className="mt-8 gap-2 h-12 px-6" onClick={() => nav(user ? "/dashboard?new=1" : "/register")} data-testid="cta-register-button">{user ? "Start a new project" : "Get started free"} <ArrowRight className="h-4 w-4" /></Button>
          </div>
        </section>

        <footer className="border-t border-white/10">
          <div className="max-w-[1200px] mx-auto px-6 h-16 flex items-center justify-between text-sm text-slate-400">
            <span className="font-display font-bold text-white">Project Management <span className="text-[#7ea6ff]">Orchestrator</span></span>
            <div className="flex items-center gap-4">
              <button onClick={() => nav("/pricing")} className="hover:text-white" data-testid="landing-footer-pricing">Pricing</button>
              <button onClick={() => nav("/terms")} className="hover:text-white" data-testid="landing-footer-terms">Terms</button>
              <button onClick={() => nav("/privacy")} className="hover:text-white" data-testid="landing-footer-privacy">Privacy</button>
              <span>Built for project managers.</span>
            </div>
          </div>
          <p className="mt-4 text-[11px] text-slate-500 max-w-3xl" data-testid="landing-pmi-disclaimer">
            Project Management Orchestrator is an independent product of Planit Orbit, LLC and is not affiliated with, sponsored by, or endorsed by the Project Management Institute, Inc. (PMI).
          </p>
        </footer>
      </div>
    </div>
  );
}
